"use client";

import { useCallback, useRef } from "react";
import { Star } from "lucide-react";
import { cn } from "@/lib/utils";

const STAR_COUNT = 5;
const MIN_RATING = 0;
const TAP_MIN_RATING = 0.5;
const MAX_RATING = 5;

function snapRating(raw: number): number {
  const stepped = Math.round(raw * 2) / 2;
  if (stepped <= 0) return MIN_RATING;
  return Math.max(TAP_MIN_RATING, Math.min(MAX_RATING, stepped));
}

function ratingFromX(x: number, width: number): number {
  if (width <= 0) return MAX_RATING;
  const clamped = Math.max(0, Math.min(x, width));
  return snapRating((clamped / width) * STAR_COUNT);
}

function starKind(index: number, rating: number): "full" | "half" | "empty" {
  if (rating <= 0) return "empty";
  if (rating >= index) return "full";
  if (rating >= index - 0.5) return "half";
  return "empty";
}

type StarIconProps = {
  kind: "full" | "half" | "empty";
  size: number;
  filledClassName?: string;
  emptyClassName?: string;
};

function StarIcon({
  kind,
  size,
  filledClassName = "text-amber-400 fill-amber-400",
  emptyClassName = "text-muted-foreground/40",
}: StarIconProps) {
  const style = { width: size, height: size };
  if (kind === "full") {
    return <Star className={filledClassName} style={style} strokeWidth={1.5} />;
  }
  if (kind === "half") {
    return (
      <span className="relative inline-flex" style={style}>
        <Star
          className={cn(emptyClassName, "absolute inset-0")}
          style={style}
          strokeWidth={1.5}
        />
        <span className="absolute inset-y-0 left-0 overflow-hidden" style={{ width: "50%" }}>
          <Star className={filledClassName} style={style} strokeWidth={1.5} />
        </span>
      </span>
    );
  }
  return <Star className={emptyClassName} style={style} strokeWidth={1.5} />;
}

type StarRatingInputProps = {
  value: number;
  onChange: (value: number) => void;
  size: number;
  className?: string;
};

function StarRatingInput({ value, onChange, size, className }: StarRatingInputProps) {
  const rowRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);

  const applyFromClientX = useCallback(
    (clientX: number) => {
      const el = rowRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      onChange(ratingFromX(clientX - rect.left, rect.width));
    },
    [onChange],
  );

  const endDrag = useCallback(() => {
    draggingRef.current = false;
  }, []);

  return (
    <div
      ref={rowRef}
      role="slider"
      aria-valuemin={TAP_MIN_RATING}
      aria-valuemax={MAX_RATING}
      aria-valuenow={value}
      aria-label={String(value)}
      className={cn(
        "flex w-full touch-none select-none justify-between px-1",
        className,
      )}
      onPointerDown={(e) => {
        draggingRef.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
        applyFromClientX(e.clientX);
      }}
      onPointerMove={(e) => {
        if (!draggingRef.current) return;
        applyFromClientX(e.clientX);
      }}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      {Array.from({ length: STAR_COUNT }, (_, i) => {
        const index = i + 1;
        return (
          <div key={index} className="pointer-events-none flex flex-1 justify-center">
            <StarIcon kind={starKind(index, value)} size={size} />
          </div>
        );
      })}
    </div>
  );
}

type StarRatingProps = {
  value: number;
  onChange?: (value: number) => void;
  size?: number;
  readOnly?: boolean;
  className?: string;
};

export function StarRating({
  value,
  onChange,
  size = 32,
  readOnly = false,
  className,
}: StarRatingProps) {
  if (!readOnly && onChange) {
    return <StarRatingInput value={value} onChange={onChange} size={size} className={className} />;
  }

  return (
    <div
      className={cn("flex items-center justify-center gap-1.5 px-1", className)}
      role="img"
      aria-label={String(value)}
    >
      {Array.from({ length: STAR_COUNT }, (_, i) => (
        <StarIcon key={i + 1} kind={starKind(i + 1, value)} size={size} />
      ))}
    </div>
  );
}
